import { useEffect } from "react";

import { useFormContext } from "react-hook-form";

import { abortRequest } from "@/abort";
import { config } from "@/config";
import {
  IISelectedProvider,
  IISubmitLeadResponse,
  IV1LeadPostRequest,
  v1LeadPost,
} from "@contract/electrify-v1";
import { useApi } from "@/hooks/useApi";
import { isError } from "@/utils";
import { getSessionId } from "@/utils/sessionId";
import { splitFullName } from "@/features/SolarForm/utils/spiltFullName";

export const useLeadSubmission = () => {
  const { getValues, setValue } = useFormContext();
  const { call } = useApi();

  const submitLead = async () => {
    const formData = getValues();
    const sessionId = getSessionId();
    const selectedSystemSize = formData.selectedSystemSize;
    const applicationType = formData.customerType;

    const serviceProviderIds: string[] = formData.selectedServiceProviders.map(
      ({ id }: IISelectedProvider) => id
    );

    const customer = {
      firstName: splitFullName(formData.fullName).firstName,
      lastName: splitFullName(formData.fullName).lastName,
      email: formData.email,
      phone: formData.contactNumber,
    };

    const leadData = [
      {
        question: "propertyType",
        answer: formData.buildingType,
      },
      {
        question: "roofType",
        answer: formData.roofMaterial,
      },
      {
        question: "contactDays",
        answer: formData.contactDayPreference,
      },
      {
        question: "contactTimes",
        answer: formData.contactTimePreference,
      },
      {
        question: "installTimeframe",
        answer: formData.timeFrame,
      },
      {
        question: "siteReadiness",
        answer: formData.siteReadiness,
      },
      {
        question: "customerRoofPitchDegrees",
        answer: formData.userRoofPitchValue,
      },
      {
        question: "customerSolarIntent",
        answer: formData.existingSolar,
      },
      {
        question: "numberOfStoreysFromCustomer",
        answer: formData.userStoreys,
      },
      {
        question: "has3PhasePower",
        answer: formData.threePhase,
      },
      {
        question: "preferredPaymentMethod",
        answer: formData.financeType,
      },
      {
        question: "currentSolarSystemSize",
        answer: formData.existingSolarSystemSize,
      },
    ].filter((item) => {
      return (
        item.answer !== null &&
        item.answer !== undefined &&
        item.answer !== "" &&
        !(Array.isArray(item.answer) && item.answer.length === 0) &&
        typeof item.answer !== "undefined"
      );
    });
    // The filter is to rule out any null/ undefined or empty values.

    const data: IV1LeadPostRequest = {
      sessionId,
      serviceProviderIds,
      selectedSystemSize,
      applicationType,
      leadData,
      customer,
    };

    const response: IISubmitLeadResponse | Error = await call(() =>
      v1LeadPost(config.apiBasePath, data)
    );

    if (isError(response)) {
      return {
        error: response as Error,
      };
    }

    setValue("selectedServiceProviders", response["selectedProviders"]);

    return {
      error: undefined,
    };
  };

  const abortPreviousRequestsIfAny = () => {
    abortRequest(`POST ${config.apiBasePath}/v1/lead`);
  };

  useEffect(() => {
    // clean up
    return () => {
      abortPreviousRequestsIfAny();
    };
  }, []);

  return {
    submitLead,
  };
};
